import { useState, useEffect, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Navigate, Outlet, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { loginRequest, tokenRequest } from "./authConfig";
import { SKIP_AUTH, USE_LOGGER } from "./config/constants";
import Chat from "./components/chat/Chat";
import ChatHistorySidebarNav from "./components/chat/ChatHistorySidebarNav";
import AdminUploads from "./components/admin/AdminUploads";
import Analysis from "./components/analysis/Analysis";
import RemediationDashboard from "./components/remediation_dashboard/RemediationDashboard";
import RemediationPage from "./components/remediation/RemediationPage";
import RemediationConfigPage from "./components/remediation_config/RemediationConfigPage";
import SettingsPage from "./components/settings/SettingsPage";
import Sidebar from "./components/layout/Sidebar";
import GlobalLogo from "./components/layout/GlobalLogo";
import { UserProvider } from "./context/UserContext";
import DisclaimerBanner from "./components/disclaimer/DisclaimerBanner";
import { getDisclaimerBannerConfig } from "./components/disclaimer/disclaimerBannerConfig";
import "../src/styles/app.css";
import { DEFAULT_SEARCH_SPACE } from "./config";
import wernerPng from "./assets/logos/werner.png";

const THEME_STORAGE_KEY = "werner_theme_mode";

function getInitialThemeMode() {
  if (typeof window === "undefined") {
    return "dark";
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);

  if (storedTheme === "light" || storedTheme === "dark") {
    return storedTheme;
  }

  return "dark";
}

function AppSidebarShell({ searchSpace, themeMode, onThemeModeChange }) {
  const location = useLocation();
  const isRemediationDashboard = location.pathname === "/analysis_alt";

  return (
    <div className="app-shared-shell">
      <Sidebar
        topOffsetPx={20}
        searchSpace={searchSpace}
        themeMode={themeMode}
        onThemeModeChange={onThemeModeChange}
        childrenFill
      >
        <ChatHistorySidebarNav />
      </Sidebar>

      <div
        className={`app-shared-content${isRemediationDashboard ? " app-shared-content--remediation-dashboard" : ""
          }`}
      >
        <Outlet />
      </div>
    </div>
  );
}

function App() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [themeMode, setThemeMode] = useState(getInitialThemeMode);
  const [authState, setAuthState] = useState({
    isLoading: false,
    error: null,
    tokens: null
  });
  const loginTriggeredRef = useRef(false);
  const tokenAcquisitionRef = useRef(false);
  const loginAttemptsRef = useRef(0);

  const normalizeSearchSpace = (value) =>
    String(value ?? DEFAULT_SEARCH_SPACE).trim().toLowerCase();

  const getInitialSearchSpace = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlSpace = urlParams.get("space");
    const localSpace =
      typeof window !== "undefined" ? localStorage.getItem("se_searchSpace") : null;
    return normalizeSearchSpace(urlSpace || localSpace || DEFAULT_SEARCH_SPACE);
  };

  const [searchSpace, setSearchSpace] = useState(getInitialSearchSpace());

  const updateSearchSpace = (newSpace) => {
    setSearchSpace(normalizeSearchSpace(newSpace));
  };

  const updateThemeMode = (nextThemeMode) => {
    setThemeMode(nextThemeMode === "light" ? "light" : "dark");
  };

  useEffect(() => {
    const normalizedThemeMode = themeMode === "light" ? "light" : "dark";

    if (themeMode !== normalizedThemeMode) {
      setThemeMode(normalizedThemeMode);
      return;
    }

    document.documentElement.dataset.theme = normalizedThemeMode;
    localStorage.setItem(THEME_STORAGE_KEY, normalizedThemeMode);
  }, [themeMode]);

  useEffect(() => {
    const normalizedStateSpace = normalizeSearchSpace(searchSpace);
    if (searchSpace !== normalizedStateSpace) {
      setSearchSpace(normalizedStateSpace);
      return;
    }

    const rawUrlSpace = searchParams.get("space");
    const normalizedUrlSpace = rawUrlSpace ? normalizeSearchSpace(rawUrlSpace) : null;

    if (normalizedUrlSpace && normalizedUrlSpace !== normalizedStateSpace) {
      setSearchSpace(normalizedUrlSpace);
      return;
    }

    if (rawUrlSpace !== normalizedStateSpace) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("space", normalizedStateSpace);
      setSearchParams(nextParams, { replace: true });
    }

    const localSpace = localStorage.getItem("se_searchSpace");
    if (localSpace !== normalizedStateSpace) {
      localStorage.setItem("se_searchSpace", normalizedStateSpace);
    }
  }, [searchSpace, searchParams, setSearchParams]);

  useEffect(() => {
    if (SKIP_AUTH) return;

    const isRedirecting =
      window.location.search.includes("code=") ||
      window.location.search.includes("error=") ||
      window.location.search.includes("state=");

    if (loginAttemptsRef.current >= 3) {
      if (USE_LOGGER) {
        console.warn("Too many login attempts, stopping auto-login");
      }
      return;
    }

    if (!instance) {
      if (USE_LOGGER) {
        console.log("MSAL instance not available yet, waiting...");
      }
      return;
    }

    if (!isAuthenticated && !loginTriggeredRef.current && !isRedirecting) {
      if (!instance.getActiveAccount() && inProgress === InteractionStatus.None) {
        loginAttemptsRef.current += 1;
        loginTriggeredRef.current = true;
        setAuthState((prev) => ({ ...prev, isLoading: true }));

        if (USE_LOGGER) {
          console.log(`Triggering login redirect... (attempt ${loginAttemptsRef.current})`);
        }

        setTimeout(() => {
          try {
            sessionStorage.setItem("msal_pre_auth_url", window.location.href);

            const enhancedLoginRequest = {
              ...loginRequest,
              loginHint: accounts[0]?.username,
            };

            instance.loginRedirect(enhancedLoginRequest);
          } catch (error) {
            if (USE_LOGGER) {
              console.error("Login redirect failed:", error);
            }
            loginTriggeredRef.current = false;
            setAuthState((prev) => ({
              ...prev,
              isLoading: false,
              error: error.message
            }));
          }
        }, 1000);
      }
    }
  }, [isAuthenticated, instance, inProgress, accounts]);

  useEffect(() => {
    const handleAuth = async () => {
      if (!isAuthenticated || accounts.length === 0) {
        tokenAcquisitionRef.current = false;
        setAuthState({ isLoading: false, error: null, tokens: null });
        return;
      }

      if (!instance.getActiveAccount() && accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
      }

      if (tokenAcquisitionRef.current) {
        return;
      }

      if (!instance) {
        if (USE_LOGGER) {
          console.log("MSAL instance not available yet, waiting...");
        }
        return;
      }

      if (inProgress !== InteractionStatus.None) {
        if (USE_LOGGER) {
          console.log("MSAL interaction in progress, waiting...");
        }
        return;
      }

      try {
        tokenAcquisitionRef.current = true;
        setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

        const account = accounts[0];
        if (USE_LOGGER) {
          console.log("Active account:", account);
        }

        const request = {
          ...tokenRequest,
          account
        };

        const response = await instance.acquireTokenSilent(request);
        if (USE_LOGGER) {
          console.log("Token acquired successfully:", {
            accessToken: response.accessToken ? "✓" : "✗",
            idToken: response.idToken ? "✓" : "✗"
          });
        }

        setAuthState({
          isLoading: false,
          error: null,
          tokens: {
            accessToken: response.accessToken,
            idToken: response.idToken,
            account: response.account
          }
        });
      } catch (error) {
        if (USE_LOGGER) {
          console.error("Token acquisition failed:", error);
        }

        if (
          error.name === "InteractionRequiredAuthError" ||
          error.name === "ConsentRequiredAuthError" ||
          error.name === "LoginRequiredAuthError"
        ) {
          if (USE_LOGGER) {
            console.log("Trying interactive token acquisition...");
          }

          try {
            sessionStorage.setItem("msal_pre_auth_url", window.location.href);
            await instance.acquireTokenRedirect({
              ...tokenRequest,
              account: accounts[0],
            });
          } catch (interactiveError) {
            if (USE_LOGGER) {
              console.error("Interactive token acquisition failed:", interactiveError);
            }
            tokenAcquisitionRef.current = false;
            setAuthState({
              isLoading: false,
              error: interactiveError.message,
              tokens: null
            });
          }
        } else {
          tokenAcquisitionRef.current = false;
          setAuthState({
            isLoading: false,
            error: error.message,
            tokens: null
          });
        }
      }
    };

    handleAuth();
  }, [isAuthenticated, accounts, instance, inProgress]);

  useEffect(() => {
    if (!instance) {
      if (USE_LOGGER) {
        console.log("MSAL instance not available yet, skipping event callback setup");
      }
      return;
    }

    const callbackId = instance.addEventCallback((event) => {
      if (USE_LOGGER) {
        console.log("MSAL Event:", event.eventType, event.payload);
      }

      if (event.eventType === "msal:loginFailure") {
        if (USE_LOGGER) {
          console.error("MSAL Login Failure", event);
        }
        loginTriggeredRef.current = false;
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
          error: event.payload?.error || "Login failed"
        }));
      }

      if (event.eventType === "msal:loginSuccess") {
        if (USE_LOGGER) {
          console.log("MSAL Login Success", event);
        }
        loginAttemptsRef.current = 0;
        setAuthState((prev) => ({ ...prev, isLoading: false, error: null }));
      }

      if (event.eventType === "msal:acquireTokenSuccess") {
        if (USE_LOGGER) {
          console.log("MSAL Token Acquire Success", event);
        }
        setAuthState((prev) => ({ ...prev, isLoading: false, error: null }));
      }

      if (event.eventType === "msal:acquireTokenFailure") {
        if (USE_LOGGER) {
          console.error("MSAL Token Acquire Failure", event);
        }
        tokenAcquisitionRef.current = false;
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
          error: event.payload?.error || "Token acquisition failed"
        }));
      }
    });

    return () => {
      if (callbackId && instance) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  if (authState.isLoading) {
    return (
      <div
        className="app-loading d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "100vh", gap: "20px" }}
      >
        <GlobalLogo className="app-global-logo--centered" themeMode={themeMode} />
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ fontSize: "1.2em", textAlign: "center" }}>
          <h3>Werner Chatbot</h3>
          <p>{!isAuthenticated ? "Redirecting to login…" : "Authenticating..."}</p>
        </div>
      </div>
    );
  }

  if (authState.error && !SKIP_AUTH) {
    return (
      <div
        className="app-loading d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "100vh", gap: "20px" }}
      >
        <GlobalLogo className="app-global-logo--centered" themeMode={themeMode} />
        <div style={{ fontSize: "1.2em", textAlign: "center" }}>
          <h3>Werner Chatbot</h3>
          <p className="text-danger">Authentication Error: {authState.error}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !SKIP_AUTH) {
    return (
      <div
        className="app-loading d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "100vh", gap: "20px" }}
      >
        <GlobalLogo className="app-global-logo--centered" themeMode={themeMode} />
        <img
          src={wernerPng}
          alt="Werner Logo"
          style={{ width: 48, height: "auto", marginBottom: 14 }}
        />
        <div style={{ fontSize: "1.2em", textAlign: "center" }}>
          <h3>Werner Chatbot</h3>
          <p>Redirecting to login…</p>
        </div>
      </div>
    );
  }

  const route = location.pathname;
  const activeDisclaimer = getDisclaimerBannerConfig({
    route,
    searchSpace,
  });

  const disclaimerHeight = activeDisclaimer?.height ?? 0;

  return (
    <div
      className="app-container"
      style={{ "--app-disclaimer-height": `${disclaimerHeight}px` }}
    >
      <DisclaimerBanner
        message={activeDisclaimer?.message}
        height={activeDisclaimer?.height}
      />

      <div className="app-route-shell">
        <UserProvider>
        <Routes>
          <Route
            path="/"
            element={<Navigate to={{ pathname: "/chat", search: location.search }} replace />}
          />

          <Route
            path="/chat"
            element={
              <Chat
                searchSpace={searchSpace}
                themeMode={themeMode}
                onThemeModeChange={updateThemeMode}
              />
            }
          />
          <Route
            element={
              <AppSidebarShell
                searchSpace={searchSpace}
                themeMode={themeMode}
                onThemeModeChange={updateThemeMode}
              />
            }
          >
            <Route
              path="/admin"
              element={
                <AdminUploads
                  searchSpace={searchSpace}
                  setSearchSpace={updateSearchSpace}
                />
              }
            />
            <Route path="/analysis" element={<Analysis searchSpace={searchSpace} />} />
            <Route
              path="/analysis_alt"
              element={<RemediationDashboard searchSpace={searchSpace} />}
            />
            <Route
              path="/remediation"
              element={<RemediationPage searchSpace={searchSpace} />}
            />
            <Route
              path="/remediation-config"
              element={<RemediationConfigPage searchSpace={searchSpace} />}
            />
            <Route
              path="/settings"
              element={<SettingsPage searchSpace={searchSpace} />}
            />
          </Route>
          <Route
            path="*"
            element={<Navigate to={{ pathname: "/chat", search: location.search }} replace />}
          />
        </Routes>
        </UserProvider>
      </div>
    </div>
  );
}

export default App;